import { Link } from "react-router-dom";
import { Head } from "@unhead/react";

const START_HERE = [
  {
    slug: "/bitcoin-dca",
    eyebrow: "Bitcoin DCA Guide",
    title: "How to DCA Bitcoin",
    description:
      "Learn how Bitcoin dollar-cost averaging works, how to choose a daily, weekly, or bi-weekly schedule, and how recurring buys can be automated.",
  },
  {
    slug: "/smart-dca",
    eyebrow: "Smart DCA Guide",
    title: "Smart DCA for Bitcoin",
    description:
      "See how Smart DCA differs from fixed-schedule buying and how SatStacker Smart Timing works inside each recurring buy window.",
  },
  {
    slug: "/calculator",
    eyebrow: "Interactive Tool",
    title: "Bitcoin DCA Calculator",
    description:
      "Compare regular DCA with historical Smart Timing results using your own recurring amount and cadence.",
  },
];

const POSTS = [
  {
    slug: "/blog/best-bitcoin-apps-2026",
    tag: "Comparison",
    tagColor: "#F7931A",
    title: "Best Bitcoin DCA Apps in 2026",
    description:
      "Compare SatStacker, River, Swan Bitcoin, Strike, and Coinbase for recurring buys, Smart DCA, self-custody options, automation, and fees.",
    date: "Updated Sep 2026",
  },
  {
    slug: "/blog/best-way-to-buy-bitcoin",
    tag: "Strategy",
    tagColor: "#7c3aed",
    title: "Best Way to Buy Bitcoin: Lump Sum vs DCA vs Smart DCA",
    description:
      "Compare three common approaches to buying Bitcoin and understand the tradeoffs between immediate exposure, recurring purchases, and Smart DCA.",
    date: "Updated Sep 2026",
  },
  {
    slug: "/blog/best-bitcoin-app",
    tag: "Beginner Guide",
    tagColor: "#059669",
    title: "Best Bitcoin App for Beginners in 2026",
    description:
      "Learn what beginners should look for in a Bitcoin app, including recurring buys, fees, self-custody, simplicity, and automation.",
    date: "Updated Sep 2026",
  },
  {
    slug: "/blog/smart-dca-vs-regular-dca",
    tag: "Case Study",
    tagColor: "#1d4ed8",
    title: "Smart DCA vs Regular DCA: 3-Month Bitcoin Case Study",
    description:
      "A historical comparison of standard bi-weekly Bitcoin DCA and SatStacker Smart Timing using the same planned budget and cadence.",
    date: "Updated Sep 2026",
  },
  {
    slug: "/blog/smart-timing-vs-supercharged",
    tag: "Comparison",
    tagColor: "#b45309",
    title: "SatStacker Smart Timing vs River Supercharged Recurring Buys",
    description:
      "Compare two different approaches to dip-aware recurring Bitcoin buying: changing execution timing versus increasing the scheduled purchase amount.",
    date: "Updated Sep 2026",
  },
];

const SUPPORTING_GUIDES = [
  {
    slug: "/articles/dca-vs-lump-sum",
    title: "Bitcoin DCA vs Lump Sum",
    description:
      "Compare market exposure, entry timing, recurring purchases, and the tradeoffs between buying all at once and spreading purchases over time.",
  },
  {
    slug: "/articles/self-custody",
    title: "Bitcoin Self-Custody",
    description:
      "Learn what self-custody means, how private keys and recovery phrases work, and the responsibilities that come with controlling your own Bitcoin.",
  },
];

export default function BlogIndex() {
  const title =
    "Bitcoin DCA Guides, Smart DCA & App Comparisons | SatStacker";

  const description =
    "Explore Bitcoin DCA guides, Smart DCA strategies, a Bitcoin DCA calculator, app comparisons, self-custody education, and recurring-buy research from SatStacker.";

  const canonical = "https://satstacker.app/blog";

  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    headline: "SatStacker Bitcoin DCA Guides",
    description,
    publisher: {
      "@type": "Organization",
      name: "SatStacker",
      url: "https://satstacker.app",
    },
    url: canonical,
    inLanguage: "en-US",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "SatStacker",
        item: "https://satstacker.app/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Bitcoin DCA Guides",
        item: canonical,
      },
    ],
  };

  return (
    <main
      style={{
        background: "#0a0a0a",
        minHeight: "100vh",
        color: "#f0ede8",
      }}
    >
      <Head>
        <title>{title}</title>

        <meta
          name="description"
          content={description}
        />

        <link
          rel="canonical"
          href={canonical}
        />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta
          property="og:description"
          content={description}
        />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="SatStacker" />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />
        <meta
          name="twitter:title"
          content={title}
        />
        <meta
          name="twitter:description"
          content={description}
        />

        <script type="application/ld+json">
          {JSON.stringify(blogSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Head>

      <div
        style={{
          maxWidth: 920,
          margin: "0 auto",
          padding: "60px 24px 80px",
        }}
      >
        <header style={{ marginBottom: 48 }}>
          <p
            style={{
              fontSize: 12,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "#F7931A",
              marginBottom: 16,
              fontWeight: 600,
            }}
          >
            SatStacker Learn
          </p>

          <h1
            style={{
              fontFamily: "Georgia, serif",
              fontSize: "2.4rem",
              fontWeight: 900,
              lineHeight: 1.15,
              color: "#f0ede8",
              marginBottom: 14,
            }}
          >
            Bitcoin DCA Guides, Tools & Comparisons
          </h1>

          <p
            style={{
              color: "rgba(240,237,232,0.55)",
              fontSize: 16,
              lineHeight: 1.75,
              margin: 0,
              maxWidth: 650,
            }}
          >
            Learn how to DCA Bitcoin, understand Smart DCA, compare recurring
            Bitcoin apps, explore self-custody, and test your own schedule with
            historical market data.
          </p>
        </header>

        {/* START HERE */}
        <section style={{ marginBottom: 56 }}>
          <div style={{ marginBottom: 20 }}>
            <p
              style={{
                fontSize: 11,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "#F7931A",
                marginBottom: 8,
                fontWeight: 700,
              }}
            >
              Start Here
            </p>

            <h2
              style={{
                fontFamily: "Georgia, serif",
                fontSize: "1.55rem",
                fontWeight: 700,
                color: "#f0ede8",
                margin: 0,
              }}
            >
              Core Bitcoin DCA resources
            </h2>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
              gap: 14,
            }}
          >
            {START_HERE.map((item) => (
              <Link
                key={item.slug}
                to={item.slug}
                style={{
                  display: "block",
                  background: "rgba(247,147,26,0.05)",
                  border: "1px solid rgba(247,147,26,0.18)",
                  borderRadius: 14,
                  padding: "22px 20px",
                  textDecoration: "none",
                }}
              >
                <p
                  style={{
                    color: "#F7931A",
                    fontSize: 10,
                    letterSpacing: "0.13em",
                    textTransform: "uppercase",
                    fontWeight: 700,
                    margin: "0 0 9px",
                  }}
                >
                  {item.eyebrow}
                </p>

                <h3
                  style={{
                    fontFamily: "Georgia, serif",
                    color: "#f0ede8",
                    fontSize: "1.16rem",
                    fontWeight: 700,
                    lineHeight: 1.3,
                    margin: "0 0 9px",
                  }}
                >
                  {item.title}
                </h3>

                <p
                  style={{
                    color: "rgba(240,237,232,0.5)",
                    fontSize: 14,
                    lineHeight: 1.65,
                    margin: 0,
                  }}
                >
                  {item.description}
                </p>
              </Link>
            ))}
          </div>
        </section>

        {/* GUIDES + COMPARISONS */}
        <section style={{ marginBottom: 56 }}>
          <div style={{ marginBottom: 20 }}>
            <p
              style={{
                fontSize: 11,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "#F7931A",
                marginBottom: 8,
                fontWeight: 700,
              }}
            >
              Guides & Comparisons
            </p>

            <h2
              style={{
                fontFamily: "Georgia, serif",
                fontSize: "1.55rem",
                fontWeight: 700,
                color: "#f0ede8",
                margin: 0,
              }}
            >
              Bitcoin buying research
            </h2>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
              gap: 16,
            }}
          >
            {POSTS.map(
              ({
                slug,
                tag,
                tagColor,
                title: postTitle,
                description: postDescription,
                date,
              }) => (
                <article
                  key={slug}
                  style={{
                    background: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.07)",
                    borderRadius: 12,
                    padding: "24px 20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                      marginBottom: 12,
                    }}
                  >
                    <span
                      style={{
                        background: tagColor,
                        color: "#fff",
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        padding: "2px 8px",
                        borderRadius: 3,
                      }}
                    >
                      {tag}
                    </span>

                    <span
                      style={{
                        color: "rgba(240,237,232,0.3)",
                        fontSize: 12,
                      }}
                    >
                      {date}
                    </span>
                  </div>

                  <h3
                    style={{
                      fontFamily: "Georgia, serif",
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      color: "#f0ede8",
                      marginBottom: 8,
                      lineHeight: 1.35,
                    }}
                  >
                    <Link
                      to={slug}
                      style={{
                        color: "#f0ede8",
                        textDecoration: "none",
                      }}
                    >
                      {postTitle}
                    </Link>
                  </h3>

                  <p
                    style={{
                      color: "rgba(240,237,232,0.5)",
                      fontSize: 14,
                      lineHeight: 1.65,
                      marginBottom: 16,
                    }}
                  >
                    {postDescription}
                  </p>

                  <Link
                    to={slug}
                    style={{
                      color: "#F7931A",
                      fontSize: 13,
                      textDecoration: "none",
                      fontWeight: 600,
                    }}
                  >
                    Read guide →
                  </Link>
                </article>
              )
            )}
          </div>
        </section>

        {/* SUPPORTING GUIDES */}
        <section style={{ marginBottom: 56 }}>
          <div style={{ marginBottom: 20 }}>
            <p
              style={{
                fontSize: 11,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: "#F7931A",
                marginBottom: 8,
                fontWeight: 700,
              }}
            >
              Bitcoin Fundamentals
            </p>

            <h2
              style={{
                fontFamily: "Georgia, serif",
                fontSize: "1.55rem",
                fontWeight: 700,
                color: "#f0ede8",
                margin: 0,
              }}
            >
              Supporting guides
            </h2>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(100%, 300px), 1fr))",
              gap: 16,
            }}
          >
            {SUPPORTING_GUIDES.map((item) => (
              <Link
                key={item.slug}
                to={item.slug}
                style={{
                  display: "block",
                  border: "1px solid rgba(255,255,255,0.07)",
                  borderRadius: 12,
                  padding: "20px",
                  textDecoration: "none",
                  background: "rgba(255,255,255,0.02)",
                }}
              >
                <h3
                  style={{
                    fontFamily: "Georgia, serif",
                    fontSize: "1.08rem",
                    fontWeight: 700,
                    color: "#f0ede8",
                    marginBottom: 8,
                  }}
                >
                  {item.title}
                </h3>

                <p
                  style={{
                    color: "rgba(240,237,232,0.48)",
                    fontSize: 14,
                    lineHeight: 1.65,
                    margin: 0,
                  }}
                >
                  {item.description}
                </p>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section
          style={{
            borderTop: "1px solid rgba(255,255,255,0.07)",
            paddingTop: 40,
          }}
        >
          <div
            style={{
              background: "rgba(247,147,26,0.06)",
              border: "1px solid rgba(247,147,26,0.18)",
              borderRadius: 14,
              padding: "28px 24px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 20,
            }}
          >
            <div style={{ maxWidth: 520 }}>
              <p
                style={{
                  fontFamily: "Georgia, serif",
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: "#f0ede8",
                  marginBottom: 6,
                }}
              >
                Want to test your own Bitcoin DCA schedule?
              </p>

              <p
                style={{
                  color: "rgba(240,237,232,0.45)",
                  fontSize: 14,
                  lineHeight: 1.65,
                  margin: 0,
                }}
              >
                Compare regular recurring buys with historical Smart Timing
                results before creating an account.
              </p>
            </div>

            <Link
              to="/calculator"
              style={{
                display: "inline-block",
                background: "#F7931A",
                color: "#0a0a0a",
                fontWeight: 700,
                fontSize: 14,
                padding: "12px 24px",
                borderRadius: 8,
                textDecoration: "none",
                whiteSpace: "nowrap",
              }}
            >
              Try the DCA Calculator →
            </Link>
          </div>
        </section>
      </div>
    </main>
  );
}
